import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { CalendarIcon } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

type UserType = {
  id: number;
  phone_number: string | null;
  username: string | null;
  country?: string | null;
};
import { CountryDropdown } from "react-country-region-selector";
import { axiosPrivate } from "@/api/axios";
import { useForm } from "react-hook-form";

function AddExpert() {
  const form = useForm({
    defaultValues: {
      firstName: "",
      middleName: "",
      lastName: "",
      phone: "",
      dob: null,
      email: "",
      password: "",
      gender: "",
      position: "",
      country: "",
      dxnNumber: "",
    }
  });
  const [users, setUsers] = useState<UserType[]>([]);
  const [filteredPhones, setFilteredPhones] = useState<UserType[]>([]);
  const [phoneInput, setPhoneInput] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    async function fetchUsers() {
      try {
        const res = await axiosPrivate.get("/users/");
        if (res.data.success && Array.isArray(res.data.data)) {
          const validUsers = res.data.data.filter((u: UserType) => typeof u.phone_number === "string" && u.phone_number);
          setUsers(validUsers);
        }
  } catch (err) {
      }
    }
    fetchUsers();
  }, []);

  useEffect(() => {
    if (phoneInput) {
      const filtered = users.filter((u: UserType) => u.phone_number && u.phone_number.includes(phoneInput));
      setFilteredPhones(filtered);
      // Only show dropdown if input is focused (controlled by onFocus/onBlur)
    } else {
      setFilteredPhones(users);
      // Do not show dropdown by default
    }
  }, [phoneInput, users]);

// Simple country code mapping
const countryCodeMap: { [key: string]: string } = {
  '+1': 'United States',
  '+44': 'United Kingdom',
  '+92': 'Pakistan',
  '+91': 'India',
  '+61': 'Australia',
  '+81': 'Japan',
  '+49': 'Germany',
  '+33': 'France',
  '+971': 'United Arab Emirates',
  '+966': 'Saudi Arabia',
  '+234': 'Nigeria',
  '+880': 'Bangladesh',
  '+86': 'China',
  '+7': 'Russia',
  '+20': 'Egypt',
  // Add more as needed
};

function getCountryFromPhone(phone: string): string {
  // Try to match the longest prefix first
  const codes = Object.keys(countryCodeMap).sort((a, b) => b.length - a.length);
  for (const code of codes) {
    if (phone.startsWith(code)) {
      return countryCodeMap[code];
    }
  }
  return '';
}

function handlePhoneSelect(phone: string) {
  setPhoneInput(phone);
  form.setValue("phone", phone);
  const user = users.find((u: UserType) => u.phone_number === phone);
  if (user) {
    form.setValue("firstName", user.username || "");
    // Autofill country using phone prefix
    const detectedCountry = getCountryFromPhone(phone);
    form.setValue("country", detectedCountry);
    // Autofill DXN Distributor Number
    // Try user.distributor_code, user.dxnNumber, or similar field
    const dxn = (user as any).distributor_code || (user as any).dxnNumber || "";
    form.setValue("dxnNumber", dxn);
  } else {
    form.setValue("dxnNumber", "");
  }
  setShowDropdown(false);
}

  function handlePhoneChange(e: React.ChangeEvent<HTMLInputElement>) {
    setPhoneInput(e.target.value);
    form.setValue("phone", e.target.value);
    setShowDropdown(true);
  }

  function handleSubmit(data: any) {
    // Find user by phone number
    const user = users.find(u => u.phone_number === data.phone);
    if (!user) {
      toast.error("User not found for the given phone number.");
      return;
    }
    axiosPrivate.put(`/users/${user.id}`, { role: "expert" })
      .then(res => {
        if (res.data.success) {
          toast.success("User promoted to expert successfully.");
        } else {
          toast.error("Failed to promote user to expert.");
        }
      })
      .catch(err => {
        toast.error("Error promoting user to expert.");
      });
  }
  return (
    <div className="space-y-8">
      <h1 className="text-2xl">Add Expert</h1>
      <Form {...form}>
        <form className="space-y-8" onSubmit={form.handleSubmit(handleSubmit)} autoComplete="off">
          <div className="grid grid-cols-2 gap-5">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel className="after:content-['*'] after:text-red-500 block after:ml-1">
                    First Name
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Enter First Name" {...field} autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="middleName"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Middle Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter Middle Name" {...field} autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter Last Name" {...field} autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="grid grid-cols-2 gap-5">
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem className="space-y-1 relative">
                  <FormLabel className="after:content-['*'] after:text-red-500 block after:ml-1">
                    Phone Number
                  </FormLabel>
                  <FormControl>
                    <div style={{ position: "relative" }}>
                      <Input
                        placeholder="Enter Phone Number"
                        type="text"
                        value={phoneInput}
                        onChange={handlePhoneChange}
                        onFocus={() => setShowDropdown(true)}
                        onBlur={e => {
                          setTimeout(() => {
                            setShowDropdown(false);
                          }, 150);
                        }}
                        autoComplete="new-password"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        data-form-type="other"
                        name="custom-phone-field"
                        ref={field.ref}
                      />
                      {showDropdown && (
                        <div style={{ position: "absolute", zIndex: 10, background: "white", border: "1px solid #eee", width: "100%", maxHeight: 180, overflowY: "auto" }}>
                          {filteredPhones.length === 0 ? (
                            <div style={{ padding: "8px", color: "#888" }}>
                              No phone numbers found.
                            </div>
                          ) : (
                            filteredPhones
                              .filter(u => typeof u.phone_number === "string" && u.phone_number)
                              .map(u => (
                                <div
                                  key={u.id}
                                  style={{ padding: "8px", cursor: "pointer" }}
                                  onMouseDown={() => handlePhoneSelect(u.phone_number as string)}
                                >
                                  {u.phone_number}
                                </div>
                              ))
                          )}
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="dob"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Date of Birth</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-[240px] pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value
                            ? (() => {
                                const date = new Date(field.value);
                                return isNaN(date.getTime())
                                  ? "Pick a date"
                                  : date.toLocaleDateString();
                              })()
                            : "Pick a date"}
                          <CalendarIcon className="ml-auto size-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value ?? undefined}
                        onSelect={field.onChange}
                        disabled={(date) =>
                          date > new Date() || date < new Date("1900-01-01")
                        }
                        captionLayout="dropdown"
                      />
                    </PopoverContent>
                  </Popover>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter Email" {...field} autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter Password"
                      type="password"
                      {...field}
                      autoComplete="new-password"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="gender"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Gender</FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select Gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="position"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Position</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter Position" {...field} autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel className="after:content-['*'] after:text-red-500 block after:ml-1">
                    Country
                  </FormLabel>
                  <FormControl>
                    <CountryDropdown
                      value={field.value}
                      onChange={field.onChange}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="dxnNumber"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>DXN Distributor Number</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter DXN Distributor Number"
                      type="number"
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      name={field.name}
                      ref={field.ref}
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <Button type="submit" disabled={!form.formState.isValid}>
            Make Expert
          </Button>
        </form>
      </Form>
    </div>
  );
}

export default AddExpert;
